import { useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useDialogFocus } from "../components/useDialogFocus.ts";

/**
 * A side drawer for the navy/blue auth/admin screens (controlplane-auth task 11.2, echoing AgentDNA's
 * `Drawer`; used by `UserAccessDrawer` in task 13 — click a member row, the drawer slides in with
 * their detail/role controls).
 *
 * A drawer is a modal surface anchored to an edge, so it shares the modal's accessibility needs: it
 * reuses the shared {@link useDialogFocus} hook (design 12.4) for focus-into / focus-trap / Escape /
 * focus-restore, and carries `role="dialog"`, `aria-modal`, `aria-labelledby`. The difference from
 * {@link Modal} is purely presentational — it slides from the `side` edge (default `right`) and uses
 * the drawer CSS — so a reader/keyboard user gets the same guarantees as any dialog.
 *
 * Controlled: the caller owns `open` and `onClose` (Escape, backdrop click, close button). Renders
 * nothing when closed.
 */

export interface DrawerProps {
  /** Whether the drawer is shown. When false the component renders nothing. */
  readonly open: boolean;
  /** Called to request a close (Escape, backdrop click, close button). */
  readonly onClose: () => void;
  /** The drawer title; also the accessible name (`aria-labelledby`). */
  readonly title: ReactNode;
  /** The drawer body. */
  readonly children: ReactNode;
  /** An optional footer region (e.g. action buttons). */
  readonly footer?: ReactNode;
  /** Which edge the drawer slides from. Defaults to `right`. */
  readonly side?: "left" | "right";
}

export function Drawer(props: DrawerProps): ReactNode {
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);
  const titleId = useId();

  if (!props.open) return null;

  // Render into document.body via a portal. The drawer's backdrop is position:fixed, which must be
  // relative to the viewport — but a fixed element is instead contained by any ancestor that has a
  // `transform`, `filter`, `perspective`, `contain` or `will-change`. The account page dims the
  // inactive factor's panel with a `filter`, and the drawer is a descendant of that panel, so
  // WITHOUT this portal the fixed backdrop would be trapped inside the (small, filtered) panel and
  // the flyout would render as a centered box instead of a full-height right panel. Portaling to
  // body guarantees the backdrop is a viewport child, so the flyout is always the right-side panel.
  return createPortal(
    <div
      className="oa-auth oa-drawer-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) props.onClose();
      }}
    >
      <div
        // `useDialogFocus` returns a `RefObject<T | null>`; React 18's DOM `ref` prop is typed as
        // the wider `LegacyRef`. The narrowing cast matches the element type exactly and keeps the
        // new files free of the ref-typing error the older dialogs carry.
        ref={dialogRef as React.Ref<HTMLDivElement>}
        className="oa-drawer"
        data-side={props.side ?? "right"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="oa-drawer__header">
          <h2 className="oa-drawer__title" id={titleId}>
            {props.title}
          </h2>
          <button
            type="button"
            className="oa-drawer__close"
            aria-label="Close panel"
            onClick={props.onClose}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <div className="oa-drawer__body">{props.children}</div>
        {props.footer !== undefined && (
          <footer className="oa-drawer__footer">{props.footer}</footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
